import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ImagePlus, Loader2, Shapes, Trash2, Type, Upload } from 'lucide-react';
import CachedImg from '../CachedImg';
import ProductGridSkeleton from '../ProductGridSkeleton';
import { ModalConfirmar } from '../ModalConfirmar';
import { esZip } from '../../utils/fuentesDeUnZip';
import {
  esArchivoDeFuente,
  type CarpetaDeElementos,
  type ElementoDeMarca,
} from '../../pages/v2/elementosDeMarcaApi';

/**
 * Lo que se ve al abrir una carpeta de Elementos de marca —
 * ADR-2026-10-19-elementos-de-marca.
 *
 * Es la vista de Estilos (`moodboards/VistaDeCarpeta`) con dos diferencias: acepta
 * tipografías además de imágenes, y las imágenes se ven ENTERAS (`object-contain` sobre
 * un fondo neutro), porque un logo recortado a cuadrado ya no es el logo. No habla con la
 * base: recibe los elementos y avisa lo que pasó.
 */

/** Lo que acepta el input de archivos: imágenes y las tipografías de `uploadBrandAsset`. */
// SVG y GIF no: el backend no los procesa y `uploadBrandAsset` los rechaza (ADR-590).
// Un .zip trae una familia entera: se suben todas sus tipografías
// (ADR-2026-10-20-un-zip-de-tipografias-se-sube-entero).
const FORMATOS = 'image/png,image/jpeg,image/webp,.heic,.heif,.woff2,.woff,.ttf,.otf,.zip';

const contador = (n: number) => (n === 0 ? 'Vacía' : n === 1 ? '1 elemento' : `${n} elementos`);

/** Una tipografía subida: su nombre escrito con ella misma, para reconocerla
 *  (ADR-2026-10-20-la-miniatura-de-la-tipografia-es-su-nombre). Es sólo para la persona: al
 *  usarla viaja la hoja del abecedario. Se registra con un nombre propio por elemento para
 *  no pisar ninguna fuente de la app. */
export const MuestraDeFuente = ({ elemento, className = '' }: { elemento: ElementoDeMarca; className?: string }) => {
  const familia = `elemento-de-marca-${elemento.id}`;
  const [lista, setLista] = useState(false);
  useEffect(() => {
    if (!elemento.url || typeof FontFace === 'undefined') return;
    let vivo = true;
    const fuente = new FontFace(familia, `url(${elemento.url})`);
    fuente
      .load()
      .then((f) => {
        document.fonts.add(f);
        if (vivo) setLista(true);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [elemento.url, familia]);
  return (
    <div className={`flex flex-col items-center justify-center gap-1 ${className}`}>
      {lista ? (
        <span
          className="line-clamp-3 max-w-full break-words px-3 text-center text-[22px] leading-[1.15] text-brand-black sm:text-[24px]"
          style={{ fontFamily: `"${familia}"` }}
        >
          {elemento.titulo}
        </span>
      ) : (
        <Type className="h-7 w-7 text-[#02062B4D]" strokeWidth={1.5} />
      )}
    </div>
  );
};

/**
 * El título de un elemento, debajo de su miniatura. Tocarlo lo edita en el lugar, como el
 * nombre de una carpeta: Enter o salir guarda, Esc cancela. Vacío no se guarda.
 */
const TituloEditable = ({
  elemento,
  onRenombrar,
}: {
  elemento: ElementoDeMarca;
  onRenombrar: (elemento: ElementoDeMarca, titulo: string) => Promise<void>;
}) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(elemento.titulo);
  const guardar = async () => {
    setEditando(false);
    const limpio = borrador.trim();
    if (!limpio || limpio === elemento.titulo) {
      setBorrador(elemento.titulo);
      return;
    }
    await onRenombrar(elemento, limpio);
  };
  if (editando) {
    return (
      <input
        autoFocus
        value={borrador}
        maxLength={80}
        onChange={(e) => setBorrador(e.target.value)}
        onBlur={() => void guardar()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setBorrador(elemento.titulo);
            setEditando(false);
          }
        }}
        aria-label="Título del elemento"
        className="relative z-[3] mx-1.5 mb-1.5 rounded-md border border-brand-primary bg-white px-1.5 py-0.5 text-[12px] text-brand-black outline-none"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setBorrador(elemento.titulo);
        setEditando(true);
      }}
      title="Cambiar el título"
      className="relative z-[3] truncate px-2 pb-1.5 pt-0.5 text-left text-[12px] font-medium text-[#02062BCC] hover:text-brand-primary"
    >
      {elemento.titulo}
    </button>
  );
};

export const VistaDeCarpetaDeElementos = ({
  carpeta,
  elementos,
  cargando,
  subiendo,
  onVolver,
  onAgregar,
  onEliminar,
  onRenombrar,
}: {
  carpeta: CarpetaDeElementos;
  elementos: ElementoDeMarca[];
  cargando: boolean;
  /** Cuántos archivos están subiendo ahora (0 = ninguno). */
  subiendo: number;
  onVolver: () => void;
  onAgregar: (files: File[]) => void | Promise<void>;
  onEliminar: (elemento: ElementoDeMarca) => Promise<void>;
  /** Cambiar el título de un elemento (ADR-2026-10-20-los-elementos-de-marca-tienen-titulo). */
  onRenombrar: (elemento: ElementoDeMarca, titulo: string) => Promise<void>;
}) => {
  const input = useRef<HTMLInputElement | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [aBorrar, setABorrar] = useState<ElementoDeMarca | null>(null);
  const [borrando, setBorrando] = useState(false);

  const vacia = !cargando && elementos.length === 0 && subiendo === 0;

  const elegirArchivos = (lista: FileList | null) => {
    const files = Array.from(lista ?? []).filter(
      // El input ya filtra, pero al soltar un archivo arrastrado no filtra nada.
      (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name) || esArchivoDeFuente(f) || esZip(f),
    );
    if (files.length) onAgregar(files);
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onVolver}
          aria-label="Volver a las carpetas"
          className="-ml-1.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#02062BB3] transition-colors hover:bg-black/[0.04]"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-[#02062BE6]">{carpeta.nombre}</p>
          <p className="text-[12.5px] text-[#02062B66]">
            {contador(elementos.length)}
            {subiendo > 0 && ` · subiendo ${subiendo}…`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-primary px-5 py-2.5 text-[13.5px] font-medium text-white transition-opacity hover:opacity-90 active:opacity-80"
        >
          <ImagePlus className="h-4 w-4" />
          Agregar archivos
        </button>
      </div>

      {vacia && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl bg-[#F4F6FB] px-4 py-3">
          <Shapes className="h-4 w-4 shrink-0 text-brand-primary" strokeWidth={1.75} />
          <p className="text-[13px] leading-relaxed text-[#02062BB3]">
            Subí acá los <span className="font-medium text-[#02062BE6]">logos, elementos gráficos y tipografías</span>{' '}
            de tu marca (PNG, JPG, WOFF, TTF u OTF, o el .zip de una familia). Después los elegís al crear con el Agente, y también aparecen
            en el Estudio de Video.
          </p>
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept={FORMATOS}
        multiple
        className="hidden"
        onChange={(e) => {
          elegirArchivos(e.target.files);
          e.target.value = '';
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setArrastrando(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          elegirArchivos(e.dataTransfer.files);
        }}
        className={`rounded-2xl transition-colors ${arrastrando ? 'bg-[#EBEFFE] ring-2 ring-[#1644EB66]' : ''}`}
      >
        {cargando ? (
          <ProductGridSkeleton count={8} gridClassName="grid grid-cols-3 gap-2 sm:grid-cols-4" tileClassName="rounded-lg" />
        ) : vacia ? (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-black/15 bg-white py-16 text-center transition-colors hover:border-[#1644EB80] hover:bg-[#EBEFFE]"
          >
            <Upload className="mb-3 h-7 w-7 text-[#02062B4D]" strokeWidth={1.5} />
            <p className="text-[14px] font-medium text-[#02062BCC]">Arrastrá tus archivos acá</p>
            <p className="mt-1 text-[12.5px] text-[#02062B66]">O tocá para elegirlos de tu dispositivo</p>
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {Array.from({ length: subiendo }, (_, i) => (
              <div key={`subiendo-${i}`} className="flex aspect-square items-center justify-center rounded-lg bg-neutral-100">
                <Loader2 className="h-4 w-4 animate-spin text-[#02062B4D]" />
              </div>
            ))}
            {elementos.map((el) => (
              <div key={el.id} className="group relative flex flex-col overflow-hidden rounded-lg bg-[#F4F5F7]">
                <div className="relative aspect-square">
                  {el.tipo === 'font' ? (
                    <MuestraDeFuente elemento={el} className="absolute inset-0" />
                  ) : el.url ? (
                    <CachedImg src={el.url} alt="" className="absolute inset-0 h-full w-full object-contain p-3" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#02062B40]">
                      <ImagePlus className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <TituloEditable elemento={el} onRenombrar={onRenombrar} />
                <button
                  type="button"
                  onClick={() => setABorrar(el)}
                  aria-label="Eliminar elemento"
                  className="absolute right-1 top-1 z-[2] inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#02062BB3] opacity-100 shadow-sm backdrop-blur transition-all hover:text-red-600 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ModalConfirmar
        abierto={aBorrar !== null}
        titulo="Eliminar el elemento"
        detalle="Se borra de tu marca, también del Estudio de Video. No se puede deshacer."
        confirmar="Eliminar"
        trabajando={borrando}
        onCancelar={() => setABorrar(null)}
        onConfirmar={async () => {
          if (!aBorrar) return;
          setBorrando(true);
          try {
            await onEliminar(aBorrar);
            setABorrar(null);
          } finally {
            setBorrando(false);
          }
        }}
      />
    </div>
  );
};

export default VistaDeCarpetaDeElementos;
